import React, { useEffect, useRef, useState } from "react";
import { collectConstellations, completedNewestFirst, savePick, clearPick } from "../data/constellations";
import { drawConstellation, waitForFonts, SIZES } from "../utils/renderConstellation";
import exportCanvas from "../utils/exportImage";
import { track } from "../utils/analytics";
import "./ConstellationModal.css";

/**
 * 읽은 책으로 별자리를 만든다.
 *
 * 두 걸음이다. 먼저 지금 무엇을 만들 수 있는지 보여주고 — 못 만드는
 * 것도 몇 권이 모자란지 함께 적는다. 그게 다음에 읽을 이유가 된다 —
 * 하나를 고르면 채워진 그림과 내보내기가 나온다.
 */
export default function ConstellationModal({ books, uid, name, onClose }) {
  const canvasRef = useRef(null);
  const modalRef = useRef(null);
  const [picked, setPicked] = useState(null);
  const [state, setState] = useState("idle"); // idle | working | manual
  const [msg, setMsg] = useState("");
  /* 규격. 4:5 는 인스타 게시물, 9:16 은 스토리다. 게시물 규격을 스토리에
     올리면 위아래가 남아 인스타가 알아서 채운다. */
  const [size, setSize] = useState("feed");
  /* 책 고르는 중이면 고르고 있는 id 목록. null 이면 안 고르는 중이다.
     고른 순서가 별 번호 순서다 — 어느 책을 어느 별에 둘지도 함께 정해진다. */
  const [choosing, setChoosing] = useState(null);
  /* 이 화면에서 방금 고친 것. 저장된 값을 다시 읽지 않고 그림에 바로 쓴다. */
  const [override, setOverride] = useState(null);

  const rows = collectConstellations(books, uid);
  const have = rows[0]?.have ?? 0;
  const readyCount = rows.filter((r) => r.ready).length;
  const allDone = completedNewestFirst(books, uid);
  /** 지금 그릴 책 */
  const fillOf = (row) => (override?.key === row.tpl.key ? override.fill : row.fill);
  const isCustom = (row) => (override?.key === row.tpl.key ? true : row.custom);

  useEffect(() => {
    /* 맨 아래 '닫기' 에 포커스를 주면 안 된다. 별자리가 18개라 목록이
       화면보다 길고, 브라우저가 그 버튼을 보이게 하려고 모달을 끝까지
       내려버려서 열자마자 마지막 줄이 보인다. 겹칩 자체에 포커스를 준다 —
       Escape 는 window 에 걸어둔 아래 리스너가 받는다. */
    modalRef.current?.focus({ preventScroll: true });
    const onKey = (e) => e.key === "Escape" && (picked ? setPicked(null) : onClose());
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, picked]);

  useEffect(() => {
    if (!picked || !canvasRef.current) return;
    let alive = true;
    // 먼저 한 번 그린다. 폰트를 기다린 뒤에야 그리면 그 사이 캔버스가
    // 빈 채로 남아 눌러야 나타나는 것처럼 보인다.
    const fill = fillOf(picked);
    drawConstellation(canvasRef.current, { tpl: picked.tpl, fill, name, size });
    waitForFonts().then(() => {
      if (alive && canvasRef.current) {
        drawConstellation(canvasRef.current, { tpl: picked.tpl, fill, name, size });
      }
    });
    return () => { alive = false; };
  }, [picked, name, size, override]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    if (!canvasRef.current) return;
    setState("working");
    setMsg("");
    try {
      const how = await exportCanvas(canvasRef.current, `muroo-${picked.tpl.name}-${SIZES[size].label}.png`);
      /* 어느 별자리를 내보냈는지만 센다. 책 제목은 보내지 않는다. */
      track("constellation_export", { name: picked.tpl.name, stars: picked.tpl.need, size });
      if (how === "manual") {
        setState("manual");
        setMsg("아래 그림을 길게 눌러 저장해주세요");
      } else {
        setState("idle");
        setMsg(how === "shared" ? "" : "저장했어요");
      }
    } catch {
      setState("idle");
      setMsg("이미지를 만들지 못했어요");
    }
  };

  /** 목록에 얹는 작은 모양. 별 자리와 선만 그린다 — 여기엔 제목이 안 들어간다. */
  const thumb = (tpl, ready) => {
    const W = 74, H = 52, P = 8;
    const p = tpl.points.map(([x, y]) => [P + x * (W - P * 2), P + y * (H - P * 2)]);
    return (
      <svg className="cm-thumb" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        {tpl.edges.map(([a, b], i) => (
          <line key={i} x1={p[a][0]} y1={p[a][1]} x2={p[b][0]} y2={p[b][1]}
            stroke={ready ? "rgba(201,168,76,.55)" : "rgba(245,240,232,.14)"} strokeWidth="0.7" />
        ))}
        {p.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={ready ? 1.7 : 1.2}
            fill={ready ? "#f5f0e8" : "rgba(245,240,232,.3)"} />
        ))}
      </svg>
    );
  };

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div className="cm-modal" role="dialog" aria-modal="true" aria-label="별자리"
        ref={modalRef} tabIndex={-1}
        onClick={(e) => e.stopPropagation()}>
        <div className="cm-head">
          <div className="cm-title">{picked ? picked.tpl.name : "✦ 내 별자리"}</div>
          <button className="cm-close" onClick={onClose} aria-label="닫기">✕</button>
        </div>

        {!picked ? (
          <>
            {/* 몇 권으로 무엇이 되는지 먼저 말한다. 목록만 던지면
                왜 어떤 것은 흐린지 알 수 없다. */}
            <div className="cm-sum">
              읽은 책 <b>{have}권</b>으로 <b>{readyCount}개</b>의 별자리를 만들 수 있어요
            </div>
            {/* 두 묶음으로 나눈다. 탄생 별자리는 자기 생일을 찾게 되는 것이
                요점이라 날짜를 함께 적고, 1월생부터 늘어놓는다. */}
            {[
              ["탄생 별자리", rows.filter((r) => r.tpl.when)],
              ["그 밖에", rows.filter((r) => !r.tpl.when)],
            ].map(([label, group]) => (
              <div key={label} className="cm-group">
                <div className="cm-group-t">{label}</div>
                <div className="cm-list">
                  {group.map((r) => (
                    <button
                      key={r.tpl.key}
                      className={`cm-row${r.ready ? "" : " off"}`}
                      onClick={() => { if (r.ready) { setPicked(r); setChoosing(null); setMsg(""); } }}
                      disabled={!r.ready}
                    >
                      {thumb(r.tpl, r.ready)}
                      <span className="cm-row-txt">
                        <span className="cm-row-name">{r.tpl.name}</span>
                        {r.tpl.when && <span className="cm-row-when">{r.tpl.when}</span>}
                        <span className="cm-row-meta">
                          {r.ready ? `별 ${r.tpl.need}개 · 만들 수 있어요` : `별 ${r.tpl.need}개 · ${r.left}권 더`}
                        </span>
                      </span>
                      {r.ready && <span className="cm-row-go">→</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {have === 0 && (
              <div className="cm-empty">
                아직 다 읽은 책이 없어요.
                <div className="cm-empty-sub">한 권을 다 읽으면 첫 별이 켜집니다.</div>
              </div>
            )}
          </>
        ) : choosing ? (
          /* 책 고르기. 별 번호는 고른 순서를 따른다 — 무엇을 어느 별에
             둘지까지 정해지므로, 누른 자리에 번호를 그대로 보여준다. */
          <>
            <div className="cm-sub">
              누른 순서대로 1번 별부터 채워집니다
            </div>
            <div className="cm-count">
              <b className={choosing.length === picked.tpl.need ? "done" : ""}>{choosing.length}</b>
              <span> / {picked.tpl.need}권</span>
              {choosing.length > 0 && (
                <button className="cm-count-clear" onClick={() => setChoosing([])}>비우기</button>
              )}
            </div>
            <div className="cm-books">
              {allDone.map((b) => {
                const at = choosing.indexOf(b.id);
                const on = at >= 0;
                const full = choosing.length >= picked.tpl.need;
                return (
                  <button
                    key={b.id}
                    className={`cm-book${on ? " on" : ""}${!on && full ? " full" : ""}`}
                    onClick={() =>
                      setChoosing((c) =>
                        c.includes(b.id)
                          ? c.filter((x) => x !== b.id)
                          : c.length >= picked.tpl.need ? c : [...c, b.id]
                      )
                    }
                  >
                    <span className="cm-book-n">{on ? at + 1 : "·"}</span>
                    <span className="cm-book-txt">
                      <span className="cm-book-t">{b.title}</span>
                      <span className="cm-book-a">{b.author}{b.done ? ` · ${b.done}` : ""}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              className="cm-export"
              disabled={choosing.length !== picked.tpl.need}
              onClick={() => {
                const byId = new Map(allDone.map((b) => [b.id, b]));
                const fill = choosing.map((id) => byId.get(id)).filter(Boolean);
                savePick(uid, picked.tpl.key, choosing);
                setOverride({ key: picked.tpl.key, fill });
                setChoosing(null);
                track("constellation_pick", { name: picked.tpl.name, stars: picked.tpl.need });
              }}
            >
              {choosing.length === picked.tpl.need
                ? "이 책들로 만들기"
                : `${picked.tpl.need - choosing.length}권 더 고르세요`}
            </button>
            <button className="cm-back" onClick={() => setChoosing(null)}>← 그만두기</button>
          </>
        ) : (
          <>
            <div className="cm-sub">
              {picked.tpl.when && <span className="cm-sub-when">{picked.tpl.when}</span>}
              {picked.tpl.sub}
            </div>
            {/* 규격을 그림 위에 둔다. 아래에 두면 내보내기를 누른 뒤에야
                눈에 들어와, 이미 나간 것을 다시 만들게 된다. */}
            <div className="cm-sizes" role="group" aria-label="규격">
              {Object.values(SIZES).map((s) => (
                <button
                  key={s.key}
                  className={`cm-size${size === s.key ? " on" : ""}`}
                  onClick={() => setSize(s.key)}
                  aria-pressed={size === s.key}
                >{s.label}</button>
              ))}
            </div>
            <canvas ref={canvasRef} className={`cm-canvas cm-${size}`} />
            <button className="cm-export" onClick={handleExport} disabled={state === "working"}>
              {state === "working" ? "만드는 중…" : "이미지 내보내기"}
            </button>
            {msg && <div className="cm-msg">{msg}</div>}
            {/* 기본은 최근에 읽은 순서다. 고정된 것이 아니라 고를 수
                있다는 것을 여기서 알린다. */}
            <div className="cm-pickbar">
              <span className="cm-pickbar-t">
                {isCustom(picked) ? "고른 책으로 채웠어요" : "최근에 읽은 순서로 채웠어요"}
              </span>
              <button
                className="cm-pickbar-b"
                onClick={() => setChoosing(fillOf(picked).map((b) => b.id))}
              >책 고르기</button>
              {isCustom(picked) && (
                <button
                  className="cm-pickbar-b ghost"
                  onClick={() => {
                    clearPick(uid, picked.tpl.key);
                    setOverride({ key: picked.tpl.key, fill: allDone.slice(0, picked.tpl.need) });
                  }}
                >되돌리기</button>
              )}
            </div>
            <button className="cm-back" onClick={() => { setPicked(null); setMsg(""); }}>
              ← 다른 별자리 고르기
            </button>
          </>
        )}

        <button className="cm-done" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}
